<template>
  <div ref="helpForum" class="help-forum">
    <!-- 顶部视觉区域 -->
    <div class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">问题求助</h1>
        <p class="hero-subtitle">分享问题，寻求解答，共同成长</p>
      </div>
    </div>

    <!-- 功能区域 -->
    <div class="forum-header">
      <div class="forum-stats">
        <div class="stat-item">
          <span class="stat-value">{{ total }}</span>
          <span class="stat-label">问题总数</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ resolvedCount }}</span>
          <span class="stat-label">已解决</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ myQuestions.length }}</span>
          <span class="stat-label">我的提问</span>
        </div>
      </div>
      <div class="forum-actions">
        <div class="search-bar">
          <i class="el-icon-search"></i>
          <input type="text" v-model="searchQuery" placeholder="搜索问题..." @input="handleSearch" />
        </div>
        <el-button type="primary" @click="showPostDialog = true" class="post-btn">
          <i class="el-icon-plus"></i> 发布问题
        </el-button>
      </div>
    </div>

    <!-- 问题列表 -->
    <div class="forum-container">
      <div class="forum-filter">
        <el-radio-group v-model="filter" @change="handleFilterChange">
          <el-radio-button label="all">全部问题</el-radio-button>
          <el-radio-button label="hot">热门问题</el-radio-button>
          <el-radio-button label="unsolved">未解决</el-radio-button>
          <el-radio-button label="solved">已解决</el-radio-button>
          <el-radio-button label="mine">我的问题</el-radio-button>
        </el-radio-group>
      </div>

      <div class="question-list">
        <div v-if="loading" class="loading-container">
          <el-skeleton :rows="10" animated />
        </div>
        <div v-else-if="filteredQuestions.length === 0" class="empty-state">
          <i class="el-icon-question"></i>
          <p>暂无相关问题</p>
          <el-button type="primary" @click="showPostDialog = true">立即发布问题</el-button>
        </div>
        <div v-else>
          <div v-for="(question) in filteredQuestions" :key="question.id" class="question-item"
            @click="viewQuestionDetail(question)">
            <div class="question-meta">
              <el-avatar :src="question.author_avatar || 'https://www.sweek.top/api/preview/personlog-avatar.jpg'"
                :size="40"></el-avatar>
              <div class="question-author">
                <span class="author-name">{{ question.author_nickname || '匿名用户' }}</span>
                <span class="post-time">{{ formatTime(question.create_time) }}</span>
              </div>
              <div class="question-status" :class="{ 'solved': question.solved }">
                {{ question.solved ? '已解决' : '未解决' }}
              </div>
            </div>
            <div class="question-content">
              <h3 class="question-title">{{ question.title }}</h3>
              <p class="question-summary">{{ question.summary }}</p>
              <div v-if="question.images && question.images.length > 0" class="question-images">
                <el-image v-for="(img, imgIndex) in question.images.slice(0, 3)" :key="imgIndex" :src="img" fit="cover"
                  class="preview-image" :preview-src-list="question.images">
                </el-image>
                <div v-if="question.images.length > 3" class="more-images">+{{ question.images.length - 3 }}</div>
              </div>
            </div>
            <div class="question-stats">
              <div class="stat">
                <i class="el-icon-view"></i>
                <span>{{ question.views }}</span>
              </div>
              <div class="stat">
                <i class="el-icon-chat-dot-round"></i>
                <span>{{ question.reply_count }}</span>
              </div>
              <div class="stat">
                <i class="el-icon-star-off"></i>
                <span>{{ question.likes }}</span>
              </div>
            </div>
          </div>

          <!-- 分页 -->
          <div class="pagination-container">
            <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
              :current-page="currentPage" :page-sizes="isMobile ? [5, 10] : [5, 10, 20, 30, 50]" :page-size="pageSize"
              :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next, jumper'" :total="total"
              :pager-count="isMobile ? 5 : 7" class="pagination-component">
            </el-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- 发布问题对话框 -->
    <el-dialog title="发布问题" :visible.sync="showPostDialog" :width="dialogWidth" :before-close="handleDialogClose"
      :append-to-body="true" :modal-append-to-body="true" custom-class="post-question-dialog">
      <el-form :model="newQuestion" :rules="rules" ref="questionForm" :label-width="formLabelWidth">
        <el-form-item label="标题" prop="title">
          <el-input v-model="newQuestion.title" placeholder="请输入问题标题"></el-input>
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <el-input type="textarea" v-model="newQuestion.content" :rows="6" placeholder="请详细描述您的问题"></el-input>
        </el-form-item>
        <el-form-item label="图片">
          <el-upload action="https://www.sweek.top/api/upload" list-type="picture-card"
            :on-preview="handlePictureCardPreview" :on-remove="handleRemove" :on-success="handleUploadSuccess"
            :before-upload="beforeUpload" multiple class="question-image-upload" name="images">
            <i class="el-icon-plus"></i>
          </el-upload>
          <el-dialog :visible.sync="dialogVisible" append-to-body>
            <img width="100%" :src="dialogImageUrl" alt="">
          </el-dialog>
        </el-form-item>
        <el-form-item label="标签">
          <el-select v-model="newQuestion.tags" multiple filterable allow-create default-first-option
            placeholder="请选择或创建标签" class="tag-select">
            <el-option v-for="tag in availableTags" :key="tag" :label="tag" :value="tag">
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showPostDialog = false">取 消</el-button>
        <el-button type="primary" @click="submitQuestion" :loading="submitting">发 布</el-button>
      </span>
    </el-dialog>
    <!-- 悬浮操作按钮 -->
    <div class="floating-actions">
      <button @click="refreshPage" class="action-btn refresh-btn" title="刷新页面">
        <i class="bi bi-arrow-clockwise"></i>
      </button>
      <button @click="scrollToTop" class="action-btn top-btn" title="回到顶部">
        <i class="bi bi-chevron-up"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { getQuestions, postQuestion } from '@/api/helpforum';

export default {
  name: 'HelpForumView',
  data() {
    return {
      // 问题列表数据
      questions: [],
      myQuestions: [],
      loading: true,
      total: 0,
      resolvedCount: 0,
      currentPage: 1,
      pageSize: 5,
      filter: 'all',
      searchQuery: '',
      windowWidth: window.innerWidth,

      // 发布问题相关
      showPostDialog: false,
      submitting: false,
      dialogVisible: false,
      dialogImageUrl: '',
      newQuestion: {
        title: '',
        content: '',
        images: [],
        tags: []
      },
      rules: {
        title: [
          { required: true, message: '请输入问题标题', trigger: 'blur' },
          { min: 5, max: 100, message: '标题长度在5到100个字符之间', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入问题内容', trigger: 'blur' },
          { min: 10, max: 2000, message: '内容长度在10到2000个字符之间', trigger: 'blur' }
        ]
      },
      availableTags: ['Vue', 'JavaScript', 'CSS', 'HTML', '前端', '后端', '数据库', '算法', '网络', '其他']
    };
  },
  computed: {
    filteredQuestions() {
      return this.questions;
    },
    userInfo() {
      return this.$store.state.userInfo || {};
    },
    // 响应式弹窗宽度
    dialogWidth() {
      if (this.windowWidth <= 576) {
        return '95%';
      } else if (this.windowWidth <= 768) {
        return '85%';
      } else if (this.windowWidth <= 992) {
        return '70%';
      } else {
        return '50%';
      }
    },
    // 响应式表单标签宽度
    formLabelWidth() {
      return this.windowWidth <= 576 ? '60px' : '80px';
    },
    // 是否为移动设备
    isMobile() {
      return this.windowWidth <= 768;
    }
  },
  activated() {
    this.$refs.helpForum.scrollTop = this.$route.meta.scrollTop
  },
  beforeRouteLeave(to, from, next) { // 离开组件的时候触发
    from.meta.scrollTop = this.$refs.helpForum.scrollTop
    next()
  },
  created() {
    this.fetchQuestions();
    window.addEventListener('resize', this.handleResize);
  },

  mounted() {
    this.handleResize();
  },

  /**
   * Vue 组件生命周期钩子 - 组件销毁前
   *
   * 在组件销毁前移除 window 的 'resize' 事件监听器，避免内存泄漏
   */
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    scrollToTop() {
      this.$refs.helpForum.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    refreshPage() {
      this.searchQuery = this.$options.data().searchQuery
      this.fetchQuestions()
    },
    // 获取问题列表
    fetchQuestions() {
      this.loading = true;
      const params = {
        page: this.currentPage,
        pageSize: this.pageSize,
        filter: this.filter,
        searchQuery: this.searchQuery,
        userId: this.userInfo.email
      };

      getQuestions(params).then(res => {
        if (res.code === 200) {
          res.data.list.forEach(item => {
            item.images = JSON.parse(item.images);
          });
          this.questions = res.data.list;
          this.total = res.data.pagination.total;
          this.resolvedCount = res.data.pagination.solvedCount;
          this.myQuestions = this.questions.filter(q => q.author_id === this.userInfo.email);
        } else {
          this.$message.error(res.message || '获取问题列表失败');
        }
        this.loading = false;
      }).catch(err => {
        console.error(err);
        this.$message.error('获取问题列表失败');
        this.loading = false;
      });
    },

    // 查看问题详情
    viewQuestionDetail(question) {
      this.$router.push({
        path: `/helpforum/detail`,
        query: { id: question.id, title: question.title }
      });
    },

    // 处理筛选变化
    handleFilterChange() {
      this.currentPage = 1;
      this.fetchQuestions();
    },

    // 处理搜索
    handleSearch() {
      this.currentPage = 1;
      this.fetchQuestions();
    },

    // 分页处理
    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchQuestions();
    },
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchQuestions();
    },

    // 图片上传相关
    handleRemove(file) {
      console.log('file：：：+ ', file)
      const index = this.newQuestion.images.indexOf(file.response.filenames[0]);
      if (index !== -1) {
        this.newQuestion.images.splice(index, 1);
      }
    },
    handlePictureCardPreview(file) {
      console.log('file：：：+ ', file)
      this.dialogImageUrl = file.url;
      this.dialogVisible = true;
    },
    handleUploadSuccess(res) {
      console.log('res：：：+ ', res)
      if (res.message == 'Files uploaded successfully') {
        this.newQuestion.images.push(res.filenames[0]);
      } else {
        this.$message.error('图片上传失败');
      }
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/');
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isImage) {
        this.$message.error('只能上传图片文件!');
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过 2MB!');
      }
      return isImage && isLt2M;
    },

    // 提交问题
    submitQuestion() {
      this.$refs.questionForm.validate((valid) => {
        if (valid) {
          this.submitting = true;
          const images = this.newQuestion.images.map(item => 'https://www.sweek.top/api/preview/' + item)
          // 构建问题数据
          const questionData = {
            title: this.newQuestion.title,
            content: this.newQuestion.content,
            images,
            tags: this.newQuestion.tags,
            authorId: this.userInfo.email
          };

          postQuestion(questionData).then(res => {
            this.submitting = false;
            if (res.code === 200) {
              this.showPostDialog = false;
              this.$message.success('问题发布成功');
              this.resetForm();
              this.fetchQuestions();
            } else {
              this.$message.error(res.message || '发布失败');
            }
          }).catch(err => {
            console.error(err);
            this.submitting = false;
            this.$message.error('发布失败，请稍后重试');
          });
        }
      });
    },

    // 重置表单
    resetForm() {
      this.newQuestion = {
        title: '',
        content: '',
        images: [],
        tags: []
      };
      if (this.$refs.questionForm) {
        this.$refs.questionForm.resetFields();
      }
    },

    // 关闭对话框
    handleDialogClose(done) {
      if (this.newQuestion.title || this.newQuestion.content || this.newQuestion.images.length > 0) {
        this.$confirm('确认关闭？未保存的内容将会丢失', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.resetForm();
          done();
        }).catch(() => { });
      } else {
        done();
      }
    },

    // 处理窗口大小变化
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    // 格式化时间
    formatTime(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;

      // 小于1分钟
      if (diff < 60 * 1000) {
        return '刚刚';
      }
      // 小于1小时
      if (diff < 60 * 60 * 1000) {
        return Math.floor(diff / (60 * 1000)) + '分钟前';
      }
      // 小于1天
      if (diff < 24 * 60 * 60 * 1000) {
        return Math.floor(diff / (60 * 60 * 1000)) + '小时前';
      }
      // 小于30天
      if (diff < 30 * 24 * 60 * 60 * 1000) {
        return Math.floor(diff / (24 * 60 * 60 * 1000)) + '天前';
      }

      // 大于30天显示具体日期
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }
};
</script>

<style lang="scss" scoped>
// 发布问题弹窗样式
:deep(.post-question-dialog) {
  .el-dialog__body {
    padding: 20px;
  }

  .el-form-item {
    margin-bottom: 18px;
  }

  .question-image-upload {
    .el-upload--picture-card {
      width: 100px;
      height: 100px;
      line-height: 100px;
    }

    .el-upload-list--picture-card .el-upload-list__item {
      width: 100px;
      height: 100px;
    }
  }

  .tag-select {
    width: 100%;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
}

// 移动端弹窗样式优化
@media (max-width: 768px) {
  :deep(.post-question-dialog) {
    .el-dialog__body {
      padding: 15px;
    }

    .el-form-item__label {
      padding-right: 5px;
      line-height: 30px;
    }

    .el-form-item__content {
      line-height: 30px;
    }

    .question-image-upload {
      .el-upload--picture-card {
        width: 80px;
        height: 80px;
        line-height: 80px;
      }

      .el-upload-list--picture-card .el-upload-list__item {
        width: 80px;
        height: 80px;
      }
    }

    .dialog-footer {
      flex-direction: column;

      .el-button {
        width: 100%;
        margin-left: 0;
      }
    }
  }
}

@media (max-width: 576px) {
  :deep(.post-question-dialog) {
    .el-dialog__body {
      padding: 10px;
    }

    .el-form-item {
      margin-bottom: 12px;
    }

    .question-image-upload {
      .el-upload--picture-card {
        width: 70px;
        height: 70px;
        line-height: 70px;
      }

      .el-upload-list--picture-card .el-upload-list__item {
        width: 70px;
        height: 70px;
      }
    }
  }
}

.help-forum {
  width: 100%;
  height: 100%;
  background-color: #f9f9f9;
  color: #333;
  border-radius: 8px;
  overflow: auto;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;

  .hero-section {
    background: linear-gradient(135deg, #6ab7ff, #4a8cca);
    color: white;
    padding: 60px 20px;
    text-align: center;
    margin-bottom: 40px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.2), transparent 70%);
      pointer-events: none;
    }

    .hero-title {
      font-size: 2.5rem;
      margin-bottom: 12px;
      font-weight: 600;
      letter-spacing: 0.5px;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .hero-subtitle {
      font-size: 1.2rem;
      opacity: 0.95;
      max-width: 600px;
      margin: 0 auto;
      line-height: 1.5;
    }
  }

  .forum-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 25px 25px;
    flex-wrap: wrap;
    gap: 25px;
    margin-top: -15px;

    .forum-stats {
      display: flex;
      gap: 30px;

      .stat-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 15px 20px;
        background-color: rgba(255, 255, 255, 0.8);
        border-radius: 16px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transition: transform 0.2s, box-shadow 0.2s;
        min-width: 100px;

        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
        }

        .stat-value {
          font-size: 1.8rem;
          font-weight: 600;
          color: #4a8cca;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 0.95rem;
          color: var(--secondary-text-color);
          font-weight: 500;
        }
      }
    }

    .forum-actions {
      display: flex;
      gap: 15px;
      align-items: center;

      .search-bar {
        width: 300px;
        position: relative;

        i {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--secondary-text-color);
        }

        input {
          padding: 10px 10px 10px 35px;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          width: 250px;
          background-color: var(--input-bg);
          color: var(--text-color);

          &:focus {
            outline: none;
            border-color: var(--primary-color);
          }
        }
      }

      .post-btn {
        background-color: rgb(118, 178, 238);
        border-color: var(--primary-color);
        border-radius: 8px;
        transition: background-color 0.3s ease;

        &:hover {
          background-color: #5a9bd5;
        }
      }
    }
  }

  .forum-container {
    padding: 0 20px 20px;

    .forum-filter {
      margin-bottom: 20px;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;

      .el-radio-group {
        display: flex;
        flex-wrap: nowrap;
        padding-bottom: 10px;
      }
    }

    .question-list {
      .loading-container {
        padding: 20px;
        background-color: var(--card-bg);
        border-radius: 8px;
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        background-color: var(--card-bg);
        border-radius: 8px;

        i {
          font-size: 4rem;
          color: var(--secondary-text-color);
          margin-bottom: 20px;
        }

        p {
          font-size: 1.2rem;
          color: var(--secondary-text-color);
          margin-bottom: 20px;
        }
      }

      .question-item {
        background-color: var(--card-bg);
        border-radius: 12px;
        padding: 20px;
        margin-bottom: 15px;
        cursor: pointer;
        transition: transform 0.3s, box-shadow 0.3s;
        border: 1px solid var(--border-color);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        .question-meta {
          display: flex;
          align-items: center;
          margin-bottom: 15px;

          .question-author {
            margin-left: 10px;
            flex-grow: 1;
            display: flex;
            flex-direction: column;

            .author-name {
              font-weight: 500;
              color: var(--text-color);
            }

            .post-time {
              font-size: 0.8rem;
              color: var(--secondary-text-color);
            }
          }

          .question-status {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 0.8rem;
            background-color: var(--error-color, #f56c6c);
            color: var(--active-text, white);
            white-space: nowrap;

            &.solved {
              background-color: var(--success-color, #67c23a);
              color: var(--active-text, white);
            }
          }
        }

        .question-content {
          .question-title {
            font-size: 1.2rem;
            margin-bottom: 10px;
            color: var(--text-color);
            word-break: break-word;
          }

          .question-summary {
            color: var(--secondary-text-color);
            margin-bottom: 15px;
            line-height: 1.5;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .question-images {
            display: flex;
            gap: 10px;
            margin-bottom: 15px;
            flex-wrap: wrap;

            .preview-image {
              width: 80px;
              height: 80px;
              border-radius: 4px;
              object-fit: cover;
            }

            .more-images {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 80px;
              height: 80px;
              background-color: var(--hover-bg, rgba(0, 0, 0, 0.05));
              border-radius: 4px;
              color: var(--secondary-text-color);
            }
          }
        }

        .question-stats {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;

          .stat {
            display: flex;
            align-items: center;
            color: var(--secondary-text-color);

            i {
              margin-right: 5px;
            }
          }
        }
      }

      .pagination-container {
        margin-top: 30px;
        display: flex;
        justify-content: center;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 10px;

        .el-pagination {
          .el-pager li {
            border-radius: 50%;
            transition: background-color 0.3s ease;

            &:hover {
              background-color: #e0e0e0;
            }
          }
        }
      }
    }
  }
}

// 响应式调整
@media (max-width: 1200px) {
  .help-forum {
    .forum-header {
      .forum-actions {
        .search-bar input {
          width: 300px;
        }
      }
    }
  }
}

@media (max-width: 992px) {
  .help-forum {
    .hero-section {
      padding: 30px 15px;

      .hero-title {
        font-size: 2rem;
      }

      .hero-subtitle {
        font-size: 1.1rem;
      }
    }

    .forum-header {
      .forum-stats {
        gap: 15px;

        .stat-item {
          .stat-value {
            font-size: 1.3rem;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .help-forum {
    .forum-header {
      flex-direction: column;
      align-items: stretch;
      padding: 0 15px 15px;

      .forum-stats {
        justify-content: space-around;
        margin-bottom: 15px;

        .stat-item {
          .stat-value {
            font-size: 1.2rem;
          }
        }
      }

      .forum-actions {
        flex-direction: column;
        gap: 10px;

        .search-bar input {
          width: 100%;
        }

        .post-btn {
          width: 100%;
        }
      }
    }

    .forum-container {
      padding: 0 15px 15px;

      .question-item {
        padding: 15px;

        .question-meta {
          flex-wrap: wrap;
          gap: 10px;

          .question-status {
            margin-left: auto;
          }
        }

        .question-content {
          .question-title {
            font-size: 1.1rem;
          }

          .question-images {
            .preview-image {
              width: 60px;
              height: 60px;
            }

            .more-images {
              width: 60px;
              height: 60px;
            }
          }
        }

        .question-stats {
          gap: 15px;
        }
      }
    }
  }
}

@media (max-width: 576px) {
  .help-forum {
    .hero-section {
      padding: 25px 10px;

      .hero-title {
        font-size: 1.8rem;
      }

      .hero-subtitle {
        font-size: 1rem;
      }
    }

    .forum-header {
      padding: 0 10px 10px;

      .forum-stats {
        .stat-item {
          .stat-value {
            font-size: 1.1rem;
          }

          .stat-label {
            font-size: 0.8rem;
          }
        }
      }
    }

    .forum-container {
      padding: 0 10px 10px;

      .question-item {
        padding: 12px;

        .question-meta {
          .question-author {
            .author-name {
              font-size: 0.9rem;
            }

            .post-time {
              font-size: 0.75rem;
            }
          }

          .question-status {
            font-size: 0.75rem;
            padding: 3px 6px;
          }
        }

        .question-content {
          .question-title {
            font-size: 1rem;
          }

          .question-summary {
            font-size: 0.9rem;
            -webkit-line-clamp: 2;
          }

          .question-images {
            .preview-image {
              width: 50px;
              height: 50px;
            }

            .more-images {
              width: 50px;
              height: 50px;
              font-size: 0.8rem;
            }
          }
        }

        .question-stats {
          .stat {
            font-size: 0.8rem;
          }
        }
      }
    }
  }
}

// 悬浮操作按钮
.floating-actions {
  position: fixed;
  bottom: 4rem;
  right: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  z-index: 100;
}

.action-btn {
  width: 3.25rem;
  height: 3.25rem;
  border: none;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s var(--transition-bounce);
  box-shadow: 0 10px 15px -3px var(--shadow-color),
    0 4px 6px -2px var(--shadow-color);
  background-color: var(--card-bg);
  color: var(--text-primary);
  font-size: 1.25rem;

  &:hover {
    transform: translateY(-5px);
  }

  &:active {
    transform: translateY(-2px);
  }
}
</style>
